import { useParams, Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ja } from "date-fns/locale";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { useRelease } from "../../hooks/useApi";
import { ImpactBadge, SurpriseBadge, MarketViewBadge } from "../../components/Badge";
import { LoadingSpinner, ErrorMessage } from "../../components/LoadingSpinner";

export function ReleaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useRelease(id ?? "");

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <ErrorMessage message="発表データの取得に失敗しました" />;
  if (!data) return null;

  const analysis = data.analysis;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <Link
          to="/releases"
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-3"
        >
          <ArrowLeft size={14} /> 発表履歴へ戻る
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{data.indicator.name}</h1>
            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
              <span className="font-bold text-gray-700">{data.indicator.country}</span>
              <span>·</span>
              <span>{data.indicator.category}</span>
              <span>·</span>
              <span>
                {format(parseISO(data.released_at), "yyyy年M月d日（EEE）HH:mm", { locale: ja })}
              </span>
            </div>
          </div>
          <Link
            to={`/indicators/${data.indicator.id}`}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 shrink-0"
          >
            指標詳細 <ExternalLink size={13} />
          </Link>
        </div>
      </div>

      {/* 発表値 */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-500 mb-4">発表結果</h2>
        <div className="grid grid-cols-3 gap-4">
          <ValueCell
            label="実績"
            value={`${data.result}${data.indicator.unit}`}
            highlight
          />
          <ValueCell
            label="市場予想"
            value={data.forecast != null ? `${data.forecast}${data.indicator.unit}` : "—"}
          />
          <ValueCell
            label="前回"
            value={data.previous != null ? `${data.previous}${data.indicator.unit}` : "—"}
          />
        </div>
        {data.surprise_direction && (
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
            <span className="text-sm text-gray-500">サプライズ：</span>
            <SurpriseBadge direction={data.surprise_direction} />
            {data.surprise_magnitude != null && (
              <span className="text-xs text-gray-500">
                (z-score: {data.surprise_magnitude.toFixed(2)})
              </span>
            )}
          </div>
        )}
      </div>

      {/* 解析結果 */}
      {analysis && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-500 mb-4">AI解析結果</h2>
          <div className="flex items-center gap-3 mb-4">
            <ImpactBadge level={analysis.impact_level} />
            <MarketViewBadge view={analysis.market_view} />
            <span className="text-xs text-gray-500">
              影響度スコア: {(analysis.impact_score * 100).toFixed(0)}%
            </span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">{analysis.summary}</p>
          <div className="text-xs text-gray-400 mt-3">
            解析日時: {format(parseISO(analysis.created_at), "yyyy/M/d HH:mm")}
          </div>
        </div>
      )}

      {!analysis && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-5 text-sm text-gray-500 text-center">
          この発表はまだ解析されていません
        </div>
      )}
    </div>
  );
}

function ValueCell({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="text-center">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={`text-xl font-bold ${highlight ? "text-blue-700" : "text-gray-800"}`}>
        {value}
      </div>
    </div>
  );
}
